/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ModuleLayout, type ModuleLayoutConfig, getModuleLayoutConfig, noImage } from '@coursera-web/cds-labs';

import {
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionPanel,
  Grid,
  ShowMoreList,
  Typography2,
  breakpoints,
  typography2,
  useMediaQuery,
} from '@coursera/cds-core';
import type { EventData } from '@coursera/event-pulse/react';
import { useTracker } from '@coursera/event-pulse/react';

import _t from 'i18n!nls/front-page';

export type FaqModuleItem = {
  question?: string;
  answer?: string | React.ReactNode;
};

type Props = {
  faqs?: FaqModuleItem[] | null;
  headline?: string | null;
  accordionTrackingData?: Omit<EventData<'expand_accordion'>, 'accordion'>;
} & Omit<ModuleLayoutConfig, 'preloadImage' | 'lazyLoadImage'>;

const MAX_FAQS_TO_SHOW = 10;

const styles = {
  faqContentWrapper: css`
    padding: 0;

    button {
      outline: none;
      padding: var(--cds-spacing-100) 0 !important;
    }

    button:focus {
      background: none;
    }
  `,
  moreInfoContentWrapper: css`
    align-self: flex-start;
    width: 100%;
    border: 2px solid var(--cds-color-grey-50);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-150);
      margin-top: var(--cds-spacing-100);
    }
  `,
  titleContainer: css`
    margin-bottom: calc(var(--cds-spacing-300) * -1);
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  faqContentContainer: css`
    margin-top: var(--cds-spacing-100);
    ${typography2.bodySecondary};
  `,
  showMoreList: css`
    .cds-ShowMoreList-listContainer {
      padding: 0;
    }
  `,
  accordionItemContainer: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  accordionItemContainerFirst: css`
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  headerSubtitle: css`
    ${typography2.subtitleSmall}
    background: transparent;

    &:hover {
      border-radius: var(--cds-border-radius-100);
    }

    &:focus {
      background: transparent;
    }
  `,
};

const FAQsModule = (props: Props) => {
  const { faqs, accordionTrackingData, headline } = props;
  const tracker = useTracker();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const filteredFaqs = faqs?.filter((faq) => !!faq?.question && !!faq?.answer);

  if (!filteredFaqs?.length) {
    return null;
  }

  const moduleLayoutConfig: ModuleLayoutConfig = getModuleLayoutConfig(props);

  const renderContent = () => (
    <Grid container spacing={isMobile ? 16 : 32}>
      <Grid item xs={12} css={styles.titleContainer}>
        <Typography2 variant="subtitleLarge" component="h2">
          {headline ?? _t('Frequently Asked Questions')}
        </Typography2>
      </Grid>
      <Grid item xs={12}>
        <div css={styles.faqContentWrapper}>
          <AccordionGroup spacing={8}>
            <ShowMoreList
              numItemsToShow={MAX_FAQS_TO_SHOW}
              controllerProps={{ itemTypePlural: _t('frequently asked questions') }}
              css={styles.showMoreList}
            >
              {filteredFaqs.map((faq, index) => {
                if (!faq.question) {
                  return null;
                } else {
                  const onChange = (_: React.ChangeEvent<unknown>, expanded: boolean) => {
                    tracker(expanded ? 'expand_accordion' : 'collapse_accordion', {
                      accordion: {
                        name: 'faq',
                        header: faq.question,
                      },
                      ...accordionTrackingData,
                    });
                  };
                  return (
                    <div
                      css={
                        index === 0
                          ? [styles.accordionItemContainer, styles.accordionItemContainerFirst]
                          : styles.accordionItemContainer
                      }
                      key={faq.question}
                    >
                      <Accordion variant="silent" onChange={onChange}>
                        <AccordionHeader label={faq.question} css={styles.headerSubtitle} />
                        <AccordionPanel>
                          <div css={styles.faqContentContainer}>{faq.answer}</div>
                        </AccordionPanel>
                      </Accordion>
                    </div>
                  );
                }
              })}
            </ShowMoreList>
          </AccordionGroup>
        </div>
      </Grid>
    </Grid>
  );

  return <ModuleLayout renderContent={renderContent} assembly={noImage(moduleLayoutConfig)} />;
};

export default FAQsModule;
